import { IClipContainer } from "./IClipContainer";

import { IGoodgameClip } from "../clip/IGoodgameClip";
import { ClipSizeComponent } from "../components/ClipSizeComponent";

import DisplayObject = createjs.DisplayObject;
import Container = createjs.Container;
import Event = createjs.Event;
import MouseEvent = createjs.MouseEvent;
import GlowFilter = createjs.GlowFilter;
import Point = createjs.Point;

import { Signal } from "signals.js";

export class BitmapClipContainer extends Container implements IClipContainer {
    private itemDict: Map<any, any>;

    private isMouseOver: boolean = false;

    private _isInteractive: boolean = false;

    public clipSizeChangedSignal: Signal = new Signal();

    private _mainClipClassName: string;

    private _clipSizeComponent: ClipSizeComponent;

    private _minSize: Point;
    private _maxSize: Point;

    private _isDisposed = false;

    constructor() {
        super();
        this.mouseEnabled = false;
        this.addEventListener(Event.ADDED_TO_STAGE, this.bindFunction(this.addedToStage), false);
    }

    public addItem(item: IGoodgameClip, index: number = -1): void {
        if (!this.itemDict) {
            this.itemDict = new Map<any, any>();
        }

        if (this._isInteractive) {
            item.addEventListener("mouseout", this.bindFunction(this.onMouseOut));
            item.addEventListener("mouseover", this.bindFunction(this.onMouseOver));
        }

        this.itemDict.set(item, item);
        if (index < 1) {
            this.addChild(item.asDisplayObject());
        } else {
            this.addChildAt(item.asDisplayObject(), index);
        }
        this.addClipSizeComponentToItem(item);
    }

    private addedToStage(e: Event): void {
        this.removeEventListener(Event.ADDED_TO_STAGE, this.bindFunction(this.addedToStage));
        this.clipSizeChangedSignal.dispatch(this);
    }

    private onChildClipSizeChanged(clip: IGoodgameClip): void {
        this.clipSizeChangedSignal.dispatch(this);
    }

    public set isInteractive(value: boolean) {
        if (value == this._isInteractive) {
            return;
        }
        if (value) {
            this.addMouseListener();
        }
        else {
            this.removeMouseListener();
        }
        this._isInteractive = value;
    }

    private addMouseListener(): void {
        if( this.itemDict ) {
            for (let item of Array.from(this.itemDict.values())) {
                item.removeEventListener("mouseout", this.bindFunction(this.onMouseOut));
                item.removeEventListener("mouseover", this.bindFunction(this.onMouseOver));
            }
        }
    }

    private removeMouseListener(): void {
        if( this.itemDict ) {
            for (let item of Array.from(this.itemDict.values())) {
                item.removeEventListener("mouseout", this.bindFunction(this.onMouseOut));
                item.removeEventListener("mouseover", this.bindFunction(this.onMouseOver));
            }
        }
        this.removeEventListener(Event.ADDED_TO_STAGE, this.addedToStage);
    }

    public removeItem(itemToRemove: IGoodgameClip): void {
        if (itemToRemove) {
            itemToRemove.dispose();
            itemToRemove.removeEventListener("mouseout", this.bindFunction(this.onMouseOut));
            itemToRemove.removeEventListener("mouseover", this.bindFunction(this.onMouseOver));
            if (itemToRemove.asDisplayObject().parent == this) {
                this.removeChild(itemToRemove.asDisplayObject());
            }
            if (this.itemDict) {
                this.itemDict.delete(itemToRemove);
            }
        }
    }

    public removeAllItems(): void {
        if( this.itemDict ) {
            for (let item of Array.from(this.itemDict.values())) {
                this.removeItem(item);
            }
        }
    }

    public isHit(posX: number, posY: number): boolean {
        if( this.itemDict ) {
            for (let item of Array.from(this.itemDict.values())) {
                if (item.isHit(posX, posY)) {
                    return true;
                }
            }
        }
        return false;
    }

    public dispose(): void {
        if (this._clipSizeComponent) {
            this._clipSizeComponent.destroy();
        }
        this.removeAllItems();
        if (this.clipSizeChangedSignal) {
            this.clipSizeChangedSignal.removeAll();
        }
        this.removeEventListener(Event.ADDED_TO_STAGE, this.bindFunction(this.addedToStage));
        this.itemDict = null;
        this._isDisposed = true;
    }

    public get isDisposed() {
        return this._isDisposed;
    }

    private onMouseOut(event: MouseEvent): void {
        event.stopImmediatePropagation();

        // TODO: @HTML5
        /*if (this.itemDict.get(event.relatedTarget) == null && this.isMouseOver) {
            this.dispatchEvent(event.clone());
            this.isMouseOver = false;
        }*/
    }

    private onMouseOver(event: MouseEvent): void {
        event.stopImmediatePropagation();

        // TODO: @HTML5
        /*if (this.itemDict.get(event.relatedTarget) == null && !this.isMouseOver) {
            this.dispatchEvent(event.clone());
            this.isMouseOver = true;
        }*/
    }

    public addGlow(): void {
        if (this.filters.length == 0) {
            this.filters = [new GlowFilter(0x0000ff, 0.5, 1, 1, 1, 1, false, false)];
        }
    }

    public removeGlow(): void {
        if (this.filters.length > 0) {
            this.filters = [];
        }
    }

    /**
     * @public Interface implementation. Retuns the BitmapClip as DisplayObject
     */
    public asDisplayObject(): DisplayObject {
        return this;
    }

    private addClipSizeComponentToItem(item: IGoodgameClip): void {
        if (this._clipSizeComponent) {
            item.clipSizeComponent = this._clipSizeComponent;
            item.clipSizeComponent.clipSizeChanged.add(this.bindFunction(this.onChildClipSizeChanged));
        }
    }

    public set clipSizeComponent(value: ClipSizeComponent) {
        this._clipSizeComponent = value;
        if (this._clipSizeComponent && this.itemDict) {
            for (let item of Array.from(this.itemDict.values())) {
                this.addClipSizeComponentToItem(item);
            }
        }
    }

    public get clipSizeComponent(): ClipSizeComponent {
        return this._clipSizeComponent;
    }

    public get mainClipClassName(): string {
        return this._mainClipClassName;
    }

    public set mainClipClassName(value: string) {
        this._mainClipClassName = value;
    }

    public get minSize(): Point {
        return this._minSize;
    }

    public set minSize(value: Point) {
        this._minSize = value;
    }

    public get maxSize(): Point {
        return this._maxSize;
    }

    public set maxSize(value: Point) {
        this._maxSize = value;
    }


    once(type: string, listener: Function, thisObject?: any, useCapture?: boolean, priority?: number): void {
    }
}
